import React, { useCallback, useMemo, useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';

import { useCreateWeatherReportMutation } from 'shared/hooks/packaging/useCreateWeatherReportMutation';
import { useDownloadWeatherReportMutation } from 'shared/hooks/packaging/useDownloadWeatherReportMutation';
import { useWeatherReportListQuery } from 'shared/hooks/packaging/useWeatherReportListQuery';
import { useWeatherReportOptionsQuery } from 'shared/hooks/packaging/useWeatherReportOptionsQuery';

import { DataTable, FormComponent, Notifications } from '~/shared';

function WeatherReportDownloadCell({ isDownloading, onDownload, row }) {
    if (row.original.isDownloadReady !== true) {
        return <div>Not Ready</div>;
    }

    return (
        <Button
            disabled={isDownloading}
            id={`WeatherReport_Download_${row.id}`}
            onClick={(event) => {
                event.stopPropagation();
                onDownload();
            }}
        >
            {isDownloading ? <CircularProgress size={20} /> : 'Download'}
        </Button>
    );
}

function WeatherReport() {
    const [notification, setNotification] = useState({ text: null, level: null });

    const {
        data: reportOptions,
        isError: isOptionsError,
        isLoading: isOptionsLoading,
    } = useWeatherReportOptionsQuery();

    const {
        data: weatherReports = [],
        isError: isListError,
        isFetching: isListFetching,
        refetch: refetchReports,
    } = useWeatherReportListQuery();

    const createReportMutation = useCreateWeatherReportMutation();
    const downloadReportMutation = useDownloadWeatherReportMutation({
        onError: () => {
            setNotification({ level: 'error', text: 'Failed to download report' });
        },
    });

    const orderSourceOpts = reportOptions?.orderSource ?? [];
    const deliveryTypeOpts = reportOptions?.deliveryType ?? [];
    const reportTypeOpts = reportOptions?.reportType ?? [];

    const formFields = useMemo(
        () => [
            {
                name: 'orderSource',
                label: 'Order Source',
                inputElement: 'autocomplete',
                gridValue: 2,
                inputProps: {
                    required: true,
                    opts: orderSourceOpts,
                    multiple: false,
                },
            },
            {
                name: 'deliveryType',
                label: 'Delivery Type',
                inputElement: 'autocomplete',
                gridValue: 2,
                inputProps: {
                    required: true,
                    opts: deliveryTypeOpts,
                    multiple: false,
                },
            },
            {
                name: 'reportType',
                label: 'Report Type',
                inputElement: 'autocomplete',
                gridValue: 2,
                inputProps: {
                    required: true,
                    opts: reportTypeOpts,
                    multiple: false,
                },
            },
        ],
        [deliveryTypeOpts, orderSourceOpts, reportTypeOpts],
    );

    const renderDownloadCell = useCallback(
        ({ row }) => (
            <WeatherReportDownloadCell
                isDownloading={downloadReportMutation.isPending && downloadReportMutation.variables === row.original.id}
                onDownload={() => downloadReportMutation.mutate(row.original.id)}
                row={row}
            />
        ),
        [downloadReportMutation],
    );

    const columns = useMemo(
        () => [
            { accessor: 'id', Header: 'Report ID' },
            { accessor: 'requestedTime', Header: 'Requested Time' },
            { accessor: 'orderSource', Header: 'Order Source' },
            { accessor: 'deliveryType', Header: 'Delivery Type' },
            { accessor: 'reportType', Header: 'Report Type' },
            { accessor: 'requestingUser', Header: 'Requesting User' },
            { accessor: 'status', Header: 'Status' },
            {
                Header: 'Download Report',
                Cell: renderDownloadCell,
            },
        ],
        [renderDownloadCell],
    );

    if (isOptionsLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <CircularProgress />
                <span className="ml-2">Loading weather report options...</span>
            </div>
        );
    }

    if (isOptionsError) {
        return (
            <div className="flex items-center justify-center p-8">
                <p className="text-red-600">Failed to load weather report options. Please try refreshing the page.</p>
            </div>
        );
    }

    return (
        <div>
            <Notifications options={notification} />
            {isListError ? (
                <Notifications options={{ level: 'error', text: 'Failed to get list of weather reports' }} />
            ) : null}
            <h1 className="text-xl font-semibold">Weather Report Instructions</h1>
            Select the following options to request a new report. Note that report generation can take over an hour.
            <ul>
                <li>
                    <b>Order Source</b>: Select the order source to include in the report. Choose <i>Misfits Market</i>{' '}
                    for 1P, <i>Blue Apron</i> or <i>Marley Spoon</i> for those meal-kit brands only, or <i>3P</i> for
                    all third-party sources (including Blue Apron and Marley Spoon).
                </li>
                <li>
                    <b>Delivery Type</b>: Select the Delivery Type that you would like to request a report for. This can
                    be DBI, Carrier, or Both.
                </li>
                <li>
                    <b>Report Type</b>: Aggregated is likely what you want because it averages Zip Codes in Hubs.
                    Not-aggregated gives data for all the Zips.
                </li>
            </ul>
            Click the Refresh Button until your report completes, then download it.
            <br />
            <br />
            <h1 className="text-xl font-semibold">Weather Reports</h1>
            <FormComponent
                formFields={formFields}
                onSubmit={async (formData, resetForm) => {
                    try {
                        await createReportMutation.mutateAsync({
                            deliveryType: formData.deliveryType,
                            orderSource: formData.orderSource,
                            reportType: formData.reportType,
                        });
                        resetForm();
                    } catch {
                        setNotification({ level: 'error', text: 'Failed to request new report' });
                    }
                }}
                button={{ text: 'Request Report', gridValue: 1 }}
                setNotification={setNotification}
            />
            <br />
            <br />
            <h1 className="text-xl font-semibold">Requested Reports</h1>
            <Button disabled={isListFetching} onClick={() => refetchReports()}>
                Refresh List
            </Button>
            <DataTable columns={columns} data={weatherReports} />
        </div>
    );
}

export { WeatherReport };
